/**
 * Draft model for a service's fixed catalog parts and part bundles (#3845).
 *
 * These live next to the required part categories (epic #3841, decision 1): a category
 * leaves an empty slot on the work order, a fixed part or bundle becomes a ready part line.
 */

import type { Service, ServicePartBundleLink, ServicePartLink } from '@/hooks/useServices';

export interface FixedPartDraft {
  /** Link id for a saved row, `draft-N` for a row not yet saved. */
  id: string;
  partId: string;
  name: string;
  sku: string;
  isActive: boolean;
  /** Current catalog price, shown for information only. */
  catalogUnitPriceCents: number;
  quantity: number;
  quantityInput: string;
  /**
   * Catalog category of the part, used for the "also a required part type" hint.
   * Null for a saved row when the API does not yet send `partCategoryId`.
   */
  categoryId: string | null;
}

export interface BundleDraft {
  id: string;
  bundleId: string;
  name: string;
  itemCount: number;
}

/** Largest quantity `service_parts.quantity` (numeric(10,2)) accepts through the API. */
export const MAX_FIXED_PART_QUANTITY = 9999.99;

/** A positive quantity with at most two decimals, or null when the input is not one. */
export function parseFixedPartQuantity(input: string): number | null {
  const value = Number.parseFloat(input.replace(',', '.'));
  if (!Number.isFinite(value) || value <= 0) return null;
  const rounded = Number(value.toFixed(2));
  if (rounded <= 0 || rounded > MAX_FIXED_PART_QUANTITY) return null;
  return rounded;
}

export function fixedPartDraftsFrom(links: ServicePartLink[] | undefined): FixedPartDraft[] {
  return (links ?? []).map((link) => ({
    id: link.id,
    partId: link.partId,
    name: link.partName,
    sku: link.partSku,
    isActive: link.partIsActive,
    catalogUnitPriceCents: link.catalogUnitPriceCents,
    quantity: link.quantity,
    quantityInput: String(link.quantity),
    categoryId: link.partCategoryId ?? null,
  }));
}

export function bundleDraftsFrom(links: ServicePartBundleLink[] | undefined): BundleDraft[] {
  return (links ?? []).map((link) => ({
    id: link.id,
    bundleId: link.bundleId,
    name: link.bundleName,
    itemCount: link.itemCount,
  }));
}

export interface ServicePartsSummaryCounts {
  categories: number;
  parts: number;
  bundles: number;
}

export function servicePartsSummaryCounts(service: Service): ServicePartsSummaryCounts {
  return {
    categories: service.partCategories.length,
    parts: service.parts?.length ?? 0,
    bundles: service.partBundles?.length ?? 0,
  };
}
